import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

import { MatSliderModule } from '@angular/material/slider';
import { NgxUsefulSwiperModule } from 'ngx-useful-swiper';

import { UIPopoverWithMenuModule } from '@app/ui/components/popover-with-menu/popover-with-menu.module';
import { UIPopoverForProductModule } from '@app/ui/components/popover-for-product/popover-for-product.module';
import { UICustomerSupportModule } from '@app/ui/components/customer-support/customer-support.module';
import { UICurrencyFormatPipeModule } from '@app/ui/pipes/currency-format/currency-format.module';
import { UIDealerHeaderModule } from '@app/ui/components/dealer-header/dealer-header.module';
import { UIIconButtonModule } from '@app/ui/components/icon-button/icon-button.module';
import { UITrustLogosModule } from '@app/ui/components/trust-logos/trust-logos.module';
import { UIConciergeModule } from '@app/ui/components/concierge/concierge.module';
import { UIIconListModule } from '@app/ui/components/icon-list/icon-list.module';
import { UIPluralisePipeModule } from '@app/ui/pipes/pluralise/pluralise.module';
import { UISeparatedPipeModule } from '@app/ui/pipes/separated/separated.module';
import { UIDateTimePipeModule } from '@app/ui/pipes/datetime/datetime.module';
import { UITooltipModule } from '@app/ui/directives/tooltip/tooltip.module';
import { UITabBarModule } from '@app/ui/components/tab-bar/tab-bar.module';
import { UIVideoModule } from '@app/ui/components/video/video.module';
import { UIMoneyPipeModule } from '@app/ui/pipes/money/money.module';
import { UIFormsModule } from '@app/ui-forms/ui-forms.module';

import { ConfigureRouterModule } from './configure.routes';

import { ConfigurePartExchangeOurPromiseComponent } from './components/part-exchange/part-exchange-our-promise/part-exchange-our-promise.component';
import { ConfigurePartExchangeVehicleComponent } from './components/part-exchange/part-exchange-vehicle/part-exchange-vehicle.component';
import { ConfigureTelephonePaymentComponent } from '@app/configure/components/payment/telephone-payment/telephone-payment.component';
import { ProgressBarStaticComponent } from './components/shared/progress-bar-static/progress-bar-static.component';
import { ConfigurePrivacyPolicyComponent } from './components/legal-info/privacy-policy/privacy-policy.component';
import { ConfigureLegalFooterLinksComponent } from './components/legal-info/footer-links/footer-links.component';
import { ConfigureCollectionFormComponent } from './components/shared/collection-form/collection-form.component';
import { ConfigureCookiePolicyComponent } from './components/legal-info/cookie-policy/cookie-policy.component';
import { ConfigureVehicleImagesComponent } from './components/shared/vehicle-images/vehicle-images.component';
import { ConfigurePurchaseVehicleComponent } from './components/purchase-vehicle/purchase-vehicle.component';
import { ConfigureCustomiseReviewComponent } from './components/customise-review/customise-review.component';
import { ConfigureDeliveryOptionsComponent } from './components/delivery-options/delivery-options.component';
import { ConfigurePaymentSenseComponent } from './components/payment/payment-sense/payment-sense.component';
import { ConfigureOrderSummaryComponent } from './components/shared/order-summary/order-summary.component';
import { ConfigureVehicleTitleComponent } from './components/shared/vehicle-title/vehicle-title.component';
import { ConfigureTermsOfUseComponent } from './components/legal-info/terms-of-use/terms-of-use.component';
import { ConfigureDeliveryFormComponent } from './components/shared/delivery-form/delivery-form.component';
import { ConfigureCallbackFormComponent } from './components/shared/callback-form/callback-form.component';
import { ConfigureFinanceOptionsComponent } from './components/finance-options/finance-options.component';
import { ConfigureFinanceFormComponent } from './components/shared/finance-form/finance-form.component';
import { ConfigurePartExchangeComponent } from './components/part-exchange/part-exchange.component';
import { ConfigureFinanceQuoteComponent } from './components/finance-quote/finance-quote.component';
import { FixedFooterComponent } from './components/shared/fixed-footer/fixed-footer.component';
import { StepHeaderComponent } from './components/shared/step-header/step-header.component';
import { ConfigureGalleryComponent } from './components/shared/gallery/gallery.component';
import { ConfigureStripeComponent } from './components/payment/stripe/stripe.component';
import { ConfigureContinueComponent } from './components/continue/continue.component';
import { StepBarComponent } from './components/shared/step-bar/step-bar.component';
import { ConfigurePaymentComponent } from './components/payment/payment.component';
import { ConfigureConsentComponent } from './components/consent/consent.component';
import { ConfigureComponent } from './components/configure.component';

import { ConfigurePartExchangeValuationModalComponent } from './components/modals/part-exchange-valuation/part-exchange-valuation.component';
import { ConfigureCustomerDetailsModalComponent } from './components/modals/customer-details/customer-details.component';
import { DemoFinanceUpdateModalComponent } from './components/modals/demo-finance-update/demo-finance-update.component';
import { ConfigureFinanceLockedModalComponent } from './components/modals/finance-locked/finance-locked.component';
import { ConfigureFinanceStatusModalComponent } from './components/modals/finance-status/finance-status.component';
import { ConfigureFinanceApplyModalComponent } from './components/modals/finance-apply/finance-apply.component';
import { ConfigureFullBreakdownComponent } from './components/modals/full-breakdown/full-breakdown.component';
import { ConfigureFinanceSkipModalComponent } from './components/modals/finance-skip/finance-skip.component';
import { ConfigureFinanceInfoModalComponent } from './components/modals/finance-info/finance-info.component';
import { ConfigureFinanceFormModalComponent } from './components/modals/finance-form/finance-form.component';
import { ConfigureSoftErrorModalComponent } from './components/modals/soft-error/soft-error.component';
import { ConfigureCallbackModalComponent } from './components/modals/callback/callback.component';
import { ConfigureWelcomeModalComponent } from './components/modals/welcome/welcome.component';
import { ConfigureAddonModalComponent } from './components/modals/addon/addon.component';

@NgModule({
	declarations: [
		ConfigurePartExchangeOurPromiseComponent,
		ConfigurePartExchangeVehicleComponent,
		ConfigureLegalFooterLinksComponent,
		ConfigureTelephonePaymentComponent,
		ConfigurePurchaseVehicleComponent,
		ConfigureCustomiseReviewComponent,
		ConfigureDeliveryOptionsComponent,
		ConfigureFinanceOptionsComponent,
		ConfigureVehicleImagesComponent,
		ConfigurePrivacyPolicyComponent,
		ConfigureOrderSummaryComponent,
		ConfigureVehicleTitleComponent,
		ConfigurePartExchangeComponent,
		ConfigureFinanceQuoteComponent,
		ConfigureCookiePolicyComponent,
		ConfigurePaymentSenseComponent,
		ConfigureCallbackFormComponent,
		ConfigureFinanceFormComponent,
		ConfigureTermsOfUseComponent,
		ConfigureContinueComponent,
		ProgressBarStaticComponent,
		ConfigureGalleryComponent,
		ConfigurePaymentComponent,
		ConfigureStripeComponent,
		ConfigureConsentComponent,
		FixedFooterComponent,
		StepHeaderComponent,
		ConfigureComponent,
		StepBarComponent,

		ConfigurePartExchangeValuationModalComponent,
		ConfigureCustomerDetailsModalComponent,
		ConfigureFinanceLockedModalComponent,
		ConfigureFinanceStatusModalComponent,
		ConfigureFinanceApplyModalComponent,
		ConfigureFinanceInfoModalComponent,
		ConfigureFinanceSkipModalComponent,
		ConfigureSoftErrorModalComponent,
		ConfigureCollectionFormComponent,
		ConfigureCallbackModalComponent,
		DemoFinanceUpdateModalComponent,
		ConfigureFullBreakdownComponent,
		ConfigureWelcomeModalComponent,
		ConfigureDeliveryFormComponent,
		ConfigureAddonModalComponent,
		ConfigureFinanceFormModalComponent,
	],
	imports: [
		NgxUsefulSwiperModule,
		MatSliderModule,
		CommonModule,
		RouterModule,

		UIPopoverWithMenuModule,
		UIPopoverForProductModule,
		UICustomerSupportModule,
		UIDealerHeaderModule,
		UIIconButtonModule,
		UITrustLogosModule,
		UIConciergeModule,
		UIIconListModule,
		UITooltipModule,
		UITabBarModule,
		UIFormsModule,
		UIVideoModule,

		UICurrencyFormatPipeModule.forRoot(),
		UIPluralisePipeModule.forRoot(),
		UISeparatedPipeModule.forRoot(),
		UIDateTimePipeModule.forRoot(),
		UIMoneyPipeModule.forRoot(),

		ConfigureRouterModule,
	],
	providers: [],
	exports: [],
})
export class ConfigureModule {}
